import initialMetricsPublisher from 'src/metrics';
import * as KatalMetrics from '@amzn/katal-metrics';

/*
 * Auth circuit breaker — guards against the 401/403 re-bootstrap loop seen in
 * P525389088 (Firefox-on-iPhone client bootstrapping ~1,422 times in 8 minutes,
 * 2,701 x 403 responses). Also matches the "Authentication loop — Repeated
 * popup to authenticate (especially Firefox) — Cognito/Federate session issue"
 * runbook entry.
 *
 * Thresholds (constants below) chosen so that:
 *   - A single flaky 401 or a race on a token refresh cannot trip the breaker.
 *   - A loop that fires the ~12-endpoint bootstrap fan-out even once with
 *     bad credentials opens the circuit on the second iteration at the latest,
 *     well before the storm scales to hundreds or thousands of requests.
 *   - After open, the circuit stays shut for 10 minutes OR until the user
 *     explicitly re-authenticates, so a persistent cookie / federation issue
 *     cannot immediately re-arm itself but a user who returns later is not
 *     greeted with a stale open circuit.
 */
const CONSECUTIVE_AUTH_FAILURE_THRESHOLD = 3;
const AUTH_FAILURE_WINDOW_MS = 60 * 1000;
const AUTO_RESET_MS = 10 * 60 * 1000;

export type AuthCircuitReason = 'consecutive-auth-failures';

export interface AuthCircuitState {
    open: boolean;
    openedAt: number | null;
    reason: AuthCircuitReason | null;
}

export class AuthCircuitOpenError extends Error {
    public readonly isAuthCircuitOpenError = true;
    constructor(message = 'Auth circuit is open; request short-circuited') {
        super(message);
        this.name = 'AuthCircuitOpenError';
    }
}

const CIRCUIT_EVENT = 'auth-circuit-change';
const target: EventTarget = new EventTarget();

let state: AuthCircuitState = {
    open: false,
    openedAt: null,
    reason: null,
};

// Sliding window of 401/403 timestamps. On every auth failure we prune
// entries older than AUTH_FAILURE_WINDOW_MS and append the new one; the
// circuit opens as soon as the window contains >= CONSECUTIVE_AUTH_FAILURE_THRESHOLD
// entries. The counter is intentionally NOT consecutive — see recordSuccess
// for the strict-vs-loose trade-off.
let authFailureTimestamps: number[] = [];
let autoResetTimer: ReturnType<typeof setTimeout> | null = null;

// Lazy — some tests mock `src/metrics` after this module is already loaded,
// and we never want metrics-publisher wiring to crash a request path.
let circuitMetricsPublisher: ReturnType<
    typeof initialMetricsPublisher.newChildActionPublisherForMethod
> | null = null;
const getCircuitMetricsPublisher = () => {
    if (!circuitMetricsPublisher) {
        circuitMetricsPublisher =
            initialMetricsPublisher.newChildActionPublisherForMethod(
                'AuthCircuitBreaker'
            );
    }
    return circuitMetricsPublisher;
};

const notify = () => {
    target.dispatchEvent(new Event(CIRCUIT_EVENT));
};

export const getCircuitState = (): AuthCircuitState => state;

export const subscribe = (listener: () => void): (() => void) => {
    target.addEventListener(CIRCUIT_EVENT, listener);
    return () => target.removeEventListener(CIRCUIT_EVENT, listener);
};

const clearAutoResetTimer = () => {
    if (autoResetTimer !== null) {
        clearTimeout(autoResetTimer);
        autoResetTimer = null;
    }
};

export const openCircuit = (reason: AuthCircuitReason): void => {
    const nextState: AuthCircuitState = {
        open: true,
        openedAt: Date.now(),
        reason,
    };
    state = nextState;

    clearAutoResetTimer();
    autoResetTimer = setTimeout(() => {
        resetCircuit();
    }, AUTO_RESET_MS);

    try {
        getCircuitMetricsPublisher().publish(
            new KatalMetrics.Metric.String('gru.authCircuit.opened', reason)
        );
    } catch (err) {
        // never let metrics publishing crash a request path
        console.error('failed to publish auth-circuit metric', err);
    }

    // Always notify so subscribers observe the refreshed openedAt, whether
    // this call is a first-open or a re-open-within-window.
    notify();
};

export const resetCircuit = (): void => {
    clearAutoResetTimer();
    authFailureTimestamps = [];
    const wasOpen = state.open;
    state = { open: false, openedAt: null, reason: null };
    if (wasOpen) {
        notify();
    }
};

const pruneWindow = (now: number): void => {
    const cutoff = now - AUTH_FAILURE_WINDOW_MS;
    while (
        authFailureTimestamps.length > 0 &&
        authFailureTimestamps[0] <= cutoff
    ) {
        authFailureTimestamps.shift();
    }
};

export const recordAuthFailure = (status: number): void => {
    const now = Date.now();
    pruneWindow(now);
    authFailureTimestamps.push(now);

    if (
        !state.open &&
        authFailureTimestamps.length >= CONSECUTIVE_AUTH_FAILURE_THRESHOLD
    ) {
        openCircuit('consecutive-auth-failures');
    }
    // status is accepted for symmetry / future tagging; counter is agnostic
    // between 401 and 403 since both indicate the same loop condition.
    void status;
};

// STRICT MODE: recordSuccess intentionally does NOT clear the sliding window
// of 401/403 timestamps. Three 401/403 responses within AUTH_FAILURE_WINDOW_MS
// open the circuit regardless of 2xx responses interleaved between them.
//
// Trade-off (strict vs loose):
//   - Loose (clear window on 2xx): tolerant of long-running mixed workloads
//     with occasional auth hiccups, but allows the P525389088 loop pattern
//     — one 2xx interleaved between every two 401/403s — to run unbounded,
//     because the counter never reaches threshold.
//   - Strict (this implementation): the "3 within 60s" intent from the
//     recovery plan is preserved even under adversarial interleaving. The
//     10-minute auto-reset and the user-initiated resetCircuit() in the
//     re-auth flow give a non-looping user a generous recovery path, so
//     strict mode does not strand legitimate users.
// We pick strict because the whole point of the breaker is to make the
// Firefox re-bootstrap loop impossible to reach.
export const recordSuccess = (): void => {
    // no-op by design
};

// Exported for tests only.
export const __testing = {
    CONSECUTIVE_AUTH_FAILURE_THRESHOLD,
    AUTH_FAILURE_WINDOW_MS,
    AUTO_RESET_MS,
};
